import ComponentInitializer from '../libs/component-initializer.js';
import ComponentBase from './base/component-base.js';
import ScrollLock from '../libs/scroll-lock';
import { createFocusTrap } from 'focus-trap';
import { scrollToElement, updateUrlHash } from '../libs/utils';

class Header extends ComponentBase {

  private menuToggle: HTMLElement;
  private menu: HTMLElement;
  private gnav: HTMLElement;
  private heroGnav: HTMLElement;
  private isOpen: boolean = false;
  private isActive: boolean = false;
  private focusTrap: any = null;
  private intersectionObserver: IntersectionObserver | null = null;

  constructor(element: HTMLElement) {
    super(element);
    this.menuToggle = this.element.querySelector('.js-menu-toggle') as HTMLElement;
    this.menu = this.element.querySelector('.js-header-menu') as HTMLElement;
    this.gnav = this.element.querySelector('.js-gnav') as HTMLElement;
    this.heroGnav = document.querySelector('.js-hero-gnav') as HTMLElement;
    // focus-trapの初期化
    this.focusTrap = createFocusTrap(this.menu, {
      initialFocus: () => {
        // メニュー内の最初のフォーカス可能要素を探す
        const firstFocusable = this.menu.querySelector('a[href], button:not([disabled])');
        return firstFocusable as HTMLElement || this.menuToggle;
      },
      fallbackFocus: this.menuToggle,
      escapeDeactivates: false, // ESCキーハンドリングを自前で行う
      returnFocusOnDeactivate: true,
      allowOutsideClick: (event: Event) => {
        // メニュートグルボタンのクリックは許可（メニューを閉じるため）
        return this.menuToggle.contains(event.target as Node);
      }
    });
    
    // イベントリスナーの設定
    this.menuToggle.addEventListener('click', () => {
      this.isOpen ? this.closeMenu() : this.openMenu();
    });
    
    // ESCキーでメニューを閉じる
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeMenu();
      }
    });
    
    // メニュー内のクリックイベント（gnav以外をクリックした時にメニューを閉じる）
    this.menu.addEventListener('click', (e) => {
      // gnavとその子要素をクリックした場合は何もしない
      if (this.gnav && (this.gnav.contains(e.target as Node) || this.gnav === e.target)) {
        return;
      }
      // gnav以外の部分をクリックした場合はメニューを閉じる
      this.closeMenu();
    });
    
    // メニュー内のアンカーリンクをクリックした時にメニューを閉じる（キャプチャフェーズ）
    if (this.menu) {
      this.menu.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        // クリックされた要素がアンカーリンク、またはアンカーリンク内の要素の場合
        const anchor = target.closest('a[href]') as HTMLAnchorElement;
        
        if (anchor) {
          const href = anchor.getAttribute('href');
          // ページ内リンク（#で始まる）の場合
          if (href && href.startsWith('#')) {
            // ignore-anchor-scrollクラスがある場合は、グローバルのスクロール処理を無視して独自処理
            if (anchor.classList.contains('ignore-anchor-scroll')) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              this.closeMenu();
              const targetId = href.slice(1);
              scrollToElement(targetId || '');
            } else {
              // ignore-anchor-scrollクラスがない場合も、メニューを閉じる
              this.closeMenu();
              // グローバルのスクロール処理に任せる
            }
          }
        }
      }, true); // true = キャプチャフェーズ
    }
    
    // heroGnavが存在する場合のみIntersection Observerを設定
    if (this.heroGnav) {
      this.setupIntersectionObserver();
    } else {
      // heroGnavが存在しない場合は常にアクティブ
      this.activate();
    }
  }
  
  private setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.heroGnav) {
            if (entry.isIntersecting) {
              // heroGnavが画面内に表示されている
              this.deactivate();
            } else {
              // heroGnavが画面から消えた
              this.activate();
            }
          }
        });
      },
      {
        root: null, // ビューポートを基準
        rootMargin: '0px',
        threshold: 0 // 少しでも見えなくなったらトリガー
      }
    );
    
    this.intersectionObserver.observe(this.heroGnav);
  }
  
  private activate() {
    if (this.isActive) return;
    this.isActive = true;
    this.element.classList.add('is-active');
  }
  
  private deactivate() {
    if (!this.isActive) return;
    this.isActive = false;
    this.element.classList.remove('is-active');
  }

  private updateAriaAttributes() {
    // ボタンのaria属性を更新
    this.menuToggle.setAttribute('aria-expanded', String(this.isOpen));
    this.menuToggle.setAttribute('aria-label', this.isOpen ? 'メニューを閉じる' : 'メニューを開く');
    
    // メニューのaria属性を更新
    this.menu.setAttribute('aria-hidden', String(!this.isOpen));
  }

  openMenu() {
    this.isOpen = true;
    this.element.classList.add('is-open');
    ScrollLock.getInstance().disable();
    this.updateAriaAttributes();
    
    // focus-trapを有効化
    if (this.focusTrap) {
      this.focusTrap.activate();
    }
  }

  closeMenu() {
    this.isOpen = false;
    this.element.classList.remove('is-open');
    ScrollLock.getInstance().enable();
    this.updateAriaAttributes();
    
    // focus-trapを無効化（自動的にreturnFocusOnDeactivateでフォーカスが戻る）
    if (this.focusTrap) {
      this.focusTrap.deactivate();
    }
  }
}

ComponentInitializer.initializeComponent('.js-header', Header);